import { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { api } from '../../lib/api';

interface QuoteSendModalProps {
  open: boolean;
  onClose: () => void;
  services: Array<{ id: string; name: string }>;
}

export default function QuoteSendModal({ open, onClose, services }: QuoteSendModalProps) {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const serviceIds = useMemo(() => services.map((service) => service.id), [services]);

  useEffect(() => {
    if (!open) {
      setNote('');
      setLoading(false);
      setSuccess(false);
      setError(null);
    }
  }, [open]);

  const handleSend = async () => {
    if (!serviceIds.length) {
      setError('Please select at least one service.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await api.post('/api/partners/quotes/send-to-self', {
        serviceIds,
        note: note.trim() || undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send Quote to My Email</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          We'll send you a formatted quote. Forward it to your customer — each service has a
          working 'Order Now' link.
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Included Services
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {services.map((service) => (
            <Chip key={service.id} label={service.name} size="small" />
          ))}
        </Stack>

        <TextField
          label="Note"
          placeholder="Add a personal note (optional)..."
          fullWidth
          multiline
          minRows={3}
          maxRows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box sx={{ width: '100%' }}>
          {success ? (
            <Alert severity="success">
              ✅ Quote sent to your email. Check your inbox and forward to your customer.
            </Alert>
          ) : (
            <Button variant="contained" onClick={handleSend} disabled={loading || !services.length}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Send to My Email'}
            </Button>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
